import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/content';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiAlertCircle, FiCheckCircle, FiArrowRight, FiUserPlus, FiLogIn } = FiIcons;

const Auth = ({ initialIsLogin = true, embedded = false }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = translations[lang].auth;
  
  const [isLogin, setIsLogin] = useState(initialIsLogin);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    setIsLogin(initialIsLogin);
  }, [initialIsLogin]);

  useEffect(() => {
    if (embedded) return; // Don't redirect if embedded

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) navigate('/steps');
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        navigate('/steps');
      }
    });

    return () => subscription?.unsubscribe();
  }, [navigate, embedded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { data: exists, error: checkError } = await supabase.rpc('check_user_exists', { email_input: email });
        
        if (checkError) {
          console.error('Error checking user existence:', checkError);
        } else if (exists) {
          throw new Error(t.userExistsError || 'An account with this email already exists. Please log in instead.');
        }

        const { error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            emailRedirectTo: window.location.origin + '/#/steps'
          }
        });
        
        if (error) {
          if (error.message.toLowerCase().includes('already registered')) {
            throw new Error(t.userExistsError || 'An account with this email already exists. Please log in instead.');
          }
          throw error;
        }

        setSuccess(lang === 'de' ? 'Registrierung erfolgreich! Bitte prüfe deine E-Mails.' : 'Signup successful! Please check your email.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={`w-full ${embedded ? '' : 'max-w-md bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden'}`}>
      {!embedded && (
        <div className="p-8 pb-0 flex flex-col items-center">
          <motion.div 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="w-20 h-20 rounded-full border-4 border-white shadow-lg overflow-hidden mb-6 bg-gray-100"
          >
            <img 
              src="https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=200&h=200" 
              alt="Mascot" 
              className="w-full h-full object-cover"
            />
          </motion.div>
          <h1 className="text-2xl font-black text-gray-900 text-center uppercase tracking-tight">
            {isLogin ? t.loginTitle : t.title}
          </h1>
          <p className="text-gray-500 text-center mt-2 font-medium">
            {isLogin ? t.loginSubtitle : t.subtitle}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`${embedded ? 'p-0' : 'p-8'} space-y-4`}>
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center space-x-2 font-medium"
            >
              <SafeIcon icon={FiAlertCircle} />
              <span>{error}</span>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-green-50 border border-green-100 text-green-600 px-4 py-3 rounded-xl text-sm flex items-center space-x-2 font-medium"
            >
              <SafeIcon icon={FiCheckCircle} />
              <span>{success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-2">
          <label className="text-xs font-black text-gray-900 uppercase tracking-widest ml-1">{t.email}</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
              <SafeIcon icon={FiMail} />
            </div>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium"
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-black text-gray-900 uppercase tracking-widest ml-1">{t.password}</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
              <SafeIcon icon={FiLock} />
            </div>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium"
              placeholder="••••••••"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white font-black py-4 rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center space-x-2 uppercase tracking-widest"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <span>{isLogin ? (lang === 'de' ? 'Anmelden' : 'Sign In') : t.submit}</span>
              <SafeIcon icon={FiArrowRight} />
            </>
          )}
        </button>

        <div className="pt-4 text-center">
          <button
            type="button"
            onClick={() => { setIsLogin(!isLogin); setError(null); setSuccess(null); }}
            className="text-blue-600 font-bold hover:text-blue-700 text-sm transition-colors"
          >
            {isLogin ? t.noAccount : t.alreadyHaveAccount}
          </button>
        </div>
      </form>
    </div>
  );

  if (embedded) return content;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 flex items-center justify-center p-4 py-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {content}
      </motion.div>
    </div>
  );
};

export default Auth;